// @custom — Global theme context (light | dark | system).
// Consumed by @system/theme.jsx via the ThemeContext / ThemeProvider bridge so
// @system shells (AppLayout, Header, Sidebar) and the Preferences page share one
// source of truth. Products embed this provider at the app root and may specialise
// its behaviour here without touching the @system theme bridge.
//
// DOM contract (see lib/@system/themeDom.js): the resolved theme is written to
// <html data-theme="light|dark"> — the switch Tailwind and brand.css key off —
// and mirrored to the legacy `.dark` class. lib/@system/brandPrePaint.js applies
// the same contract before first paint; this provider takes over after hydration.
//
// Server-rendered documents (lib/@system/initialState isServerRendered): the server
// cannot read localStorage or prefers-color-scheme, so it rendered with the brand's
// default theme. The provider starts from that same value, hydrates, and only then
// adopts the stored preference + OS scheme in a mount effect — the markup React
// checks against the server HTML (Header's theme icon and aria-label) matches, and
// <html data-theme> is written once, from the adopted value, never from the default
// (brandPrePaint already applied the right theme before first paint).

import { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react'
import { info } from '@/config'
import {
  VALID_THEMES as THEMES,
  isThemePreference,
  getSystemTheme as readSystemTheme,
  readStoredTheme,
  writeStoredTheme,
  applyResolvedTheme,
} from '@/app/lib/@system/themeDom'
import { isServerRendered } from '@/app/lib/@system/initialState'
import { BrandKit } from '@custom/BrandKit'

/** @typedef {import('@/app/lib/@system/themeDom').ThemePreference} ThemePreference */
/** @typedef {import('@/app/lib/@system/themeDom').ResolvedTheme} ResolvedTheme */

/**
 * @typedef {Object} ThemeContextValue
 * @property {ThemePreference} theme Stored preference
 * @property {ResolvedTheme} resolvedTheme What the document shows ('system' resolved)
 * @property {(next: string) => void} setTheme Ignores values outside VALID_THEMES
 */

export const ThemeContext = createContext(/** @type {ThemeContextValue | null} */ (null))

/** @type {ThemePreference[]} */
export const VALID_THEMES = [...THEMES]

/**
 * Brand default from @custom/info.js (brand.json defaultTheme), 'light' when unset/invalid.
 * @returns {ThemePreference}
 */
export const getDefaultTheme = () => {
  const preferred = info?.defaultTheme
  return isThemePreference(preferred) ? preferred : 'light'
}

/** @returns {ThemePreference} */
export const getInitialTheme = () => {
  const fallback = getDefaultTheme()
  if (typeof window === 'undefined' || isServerRendered()) return fallback
  return readStoredTheme() ?? fallback
}

export const getSystemTheme = () => readSystemTheme()

/** @param {WithChildren} props */
export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getInitialTheme)
  // `adopted` is false only while hydrating server HTML: stored preference + OS scheme are read
  // in the effect below instead of the initialisers (see the header comment).
  const [adopted, setAdopted] = useState(() => !isServerRendered())
  const [systemDark, setSystemDark] = useState(() => (isServerRendered() ? false : readSystemTheme() === 'dark'))

  useEffect(() => {
    if (adopted) return
    const stored = readStoredTheme()
    if (stored) setThemeState(stored)
    setSystemDark(readSystemTheme() === 'dark')
    setAdopted(true)
  }, [adopted])

  // Track OS colour-scheme so "system" stays responsive to live changes.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined
    }
    const mql = window.matchMedia('(prefers-color-scheme: dark)')
    if (!mql) return undefined
    /** @param {MediaQueryListEvent} event */
    const onChange = (event) => setSystemDark(!!event.matches)
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange)
      return () => mql.removeEventListener('change', onChange)
    }
    // Older Safari / jsdom fallback
    if (typeof mql.addListener === 'function') {
      mql.addListener(onChange)
      return () => mql.removeListener(onChange)
    }
    return undefined
  }, [])

  const setTheme = useCallback(
    /** @param {string} nextTheme */
    (nextTheme) => {
      if (!isThemePreference(nextTheme)) return
      setThemeState(nextTheme)
      writeStoredTheme(nextTheme)
    }, [])

  /** @type {ResolvedTheme} */
  const resolvedTheme = theme === 'system' ? (systemDark ? 'dark' : 'light') : theme

  // Keep <html data-theme> + .dark in sync with the resolved theme (once the stored
  // preference has been adopted — see the header comment).
  useEffect(() => {
    if (adopted) applyResolvedTheme(resolvedTheme)
  }, [adopted, resolvedTheme])

  /** @type {ThemeContextValue} */
  const value = useMemo(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme],
  )

  return <ThemeContext.Provider value={value}><BrandKit>{children}</BrandKit></ThemeContext.Provider>
}

/** @returns {ThemeContextValue} */
export function useThemeContext() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('UseThemeContext must be used inside <ThemeProvider>')
  return ctx
}
